import { useState, useEffect, useCallback, useRef } from 'react';
import type { GameState } from '../poker/types';
import { 
  createPlayers, 
  initializeGame, 
  advancePhase, 
  determineWinner,
  evaluateHand,
  calculateWinProbability,
  calculateOuts,
  makeBotDecision,
  processAction,
  isRoundComplete,
  getActivePlayers,
  canPlayerCheck
} from '../poker/gameLogic';
import { Card, CardStack } from './Card';
import { PlayersRow } from './PlayerAvatar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { createClient } from '@/lib/supabase';
import { RotateCcw, X, Trophy, Coins, Settings, Info, LogOut } from 'lucide-react';
import { useAuth } from '@/context/auth-context';
import { useNavigate } from 'react-router-dom';

// Temps entre accions dels bots (ms)
const BOT_THINK_TIME_MIN = 1200;
const BOT_THINK_TIME_MAX = 2500;
const PHASE_TRANSITION_DELAY = 1500;
const SHOWDOWN_DELAY = 2000;

export function PokerTable() {
  const { user, updateChips } = useAuth();
  const navigate = useNavigate();
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [showRaiseMenu, setShowRaiseMenu] = useState(false);
  const [showHandsGuide, setShowHandsGuide] = useState(false);
  const [isClosingHandsGuide, setIsClosingHandsGuide] = useState(false);
  const [showOutsInfo, setShowOutsInfo] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isClosingSettings, setIsClosingSettings] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [isClosingInfo, setIsClosingInfo] = useState(false);
  const [showProbabilities, setShowProbabilities] = useState(true);
  const [gamesPlayed, setGamesPlayed] = useState(0); // Començar a 0, representarà la ronda actual
  const [totalGames, setTotalGames] = useState(10);
  const [initialChips, setInitialChips] = useState(0); // Iniciar a 0 per defecte (mode gratuït)
  const [currentDealerIndex, setCurrentDealerIndex] = useState(0);
  const [playerChips, setPlayerChips] = useState<Record<string, number>>({});
  const [showOutOfChipsModal, setShowOutOfChipsModal] = useState(false);
  const [isSpectator, setIsSpectator] = useState(false);
  const [showFinalSummary, setShowFinalSummary] = useState(false);
  const processingRef = useRef(false);
  const hasStartedFirstGameRef = useRef(false);
  const configLoadedRef = useRef(false);
  const hasShownOutOfChipsRef = useRef(false);
  const chipsBeforeBuyInRef = useRef<number | null>(null);
  const botsOutRef = useRef<Set<string>>(new Set()); // Registre de bots que no poden jugar més
  const isStartingGameRef = useRef(false); // Protecció per evitar múltiples crides a startGame
  
  // Mostrar missatge temporal
  const showTemporaryMessage = useCallback((msg: string, duration = 1500) => {
    setMessage(msg);
    setTimeout(() => setMessage(null), duration);
  }, []);
  
  // Iniciar partida
  const startGame = useCallback(async () => {
    // Protecció per evitar múltiples crides simultànies
    if (isStartingGameRef.current) {
      console.log('⚠️ PRÀCTICA - startGame ja està en execució, saltant...');
      return;
    }
    
    isStartingGameRef.current = true;
    
    try {
      // Llegir configuració directament de localStorage per assegurar-nos que tenim el valor correcte
      const config = localStorage.getItem('practica_config');
      let currentInitialChips = initialChips;
      let currentTotalGames = totalGames;
      
      if (config) {
        try {
          const parsed = JSON.parse(config);
          if (parsed.initialChips !== undefined) {
            currentInitialChips = parsed.initialChips;
            setInitialChips(parsed.initialChips); // Actualitzar l'estat també
          }
          if (parsed.totalGames !== undefined) {
            currentTotalGames = parsed.totalGames;
            setTotalGames(parsed.totalGames); // Actualitzar l'estat també
          }
        } catch (error) {
          console.error('Error reading config in startGame:', error);
        }
      }
      
      // Llegir gamesPlayed actual abans d'incrementar
      const currentGamesPlayed = gamesPlayed;
      const nextRound = currentGamesPlayed + 1;
      
      // Si ja s'han jugat totes les rondes, no iniciar nova partida
      if (nextRound > currentTotalGames) {
        console.log('🚫 PRÀCTICA - Ja s\'han jugat totes les rondes:', { currentRound: currentGamesPlayed, nextRound, total: currentTotalGames });
        isStartingGameRef.current = false;
        return;
      }
      
      // Actualitzar el comptador
      setGamesPlayed(nextRound);
      hasStartedFirstGameRef.current = true;
      
      console.log('🎮 PRÀCTICA - Iniciant ronda:', { round: nextRound, total: currentTotalGames, previousRound: currentGamesPlayed });
    
    let players = createPlayers('Tu', 3); // 3 bots + 1 humà = 4 jugadors totals
    
    // Filtrar bots que no poden jugar més (han fet all-in i han perdut a rondes anteriors)
    players = players.filter(p => {
      if (p.isHuman) return true;
      // Si el bot està a la llista de bots que no poden jugar més, eliminar-lo
      if (botsOutRef.current.has(p.id)) {
        console.log(`🚫 Bot ${p.name} (${p.id}) no pot jugar més - eliminat de la partida`);
        return false;
      }
      return true;
    });
    
    // Si ja hi ha fitxes guardades, utilitzar-les
    if (Object.keys(playerChips).length > 0) {
      // També filtrar bots que tenen 0 fitxes a playerChips
      players = players.filter(p => {
        if (p.isHuman) return true;
        const chips = playerChips[p.id];
        if (chips !== undefined && chips === 0) {
          botsOutRef.current.add(p.id); // Afegir a la llista permanent
          console.log(`🚫 Bot ${p.name} (${p.id}) sense fitxes - afegit a la llista de bots eliminats`);
          return false;
        }
        return true;
      });
      
      // Llegir configuració per assegurar-nos que tenim el valor correcte
      const configForRounds = localStorage.getItem('practica_config');
      let currentInitialChipsForRounds = initialChips;
      if (configForRounds) {
        try {
          const parsed = JSON.parse(configForRounds);
          if (parsed.initialChips !== undefined) {
            currentInitialChipsForRounds = parsed.initialChips;
          }
        } catch (error) {
          console.error('Error reading config in rounds:', error);
        }
      }
      
      const gameChipsForRounds = currentInitialChipsForRounds === 0 ? 200 : currentInitialChipsForRounds;
      
      players = players.map(p => ({
        ...p,
        chips: Math.max(0, playerChips[p.id] || gameChipsForRounds),
        folded: false, // Reset folded per a la nova partida
        isAllIn: false // Reset all-in
      }));
    } else {
      // Primera partida: carregar fitxes reals, guardar chips_before_buy_in i jugar amb initialChips
      // Si initialChips = 0, mode gratuït: no es descompten fitxes reals i es juguen amb 200 fitxes virtuals
      const gameChips = currentInitialChips === 0 ? 200 : currentInitialChips;
      
      // Filtrar bots que no poden jugar més (han fet all-in i han perdut)
      const botsOut = players.filter(p => !p.isHuman && p.folded && p.chips === 0);
      if (botsOut.length > 0) {
        console.log('🚫 Bots que no poden jugar més:', botsOut.map(b => b.name));
      }
      
      if (currentInitialChips === 0) {
        // Mode gratuït: no es comproven ni es descompten fitxes reals
        // No es requereix usuari per jugar en mode gratuït
        console.log('🎮 PRÀCTICA - MODE GRATUÏT: Jugant amb 200 fitxes virtuals', { user: user ? 'autenticat' : 'no autenticat' });
        chipsBeforeBuyInRef.current = null; // No cal guardar res per mode gratuït
      } else if (user && currentInitialChips > 0) {
        // Carregar fitxes actuals de la base de dades i guardar chips_before_buy_in
        const supabase = createClient();
        const { data: profile } = await supabase
          .from('profiles')
          .select('chips')
          .eq('username', user.username)
          .single();
        
        if (profile) {
          const currentChips = Math.max(0, profile.chips);
          
          // Comprovar que l'usuari tingui fitxes suficients (només en mode amb fitxes reals)
          if (currentChips < currentInitialChips) {
            console.log('❌ PRÀCTICA - Fitxes insuficients:', { currentChips, initialChips: currentInitialChips });
            showTemporaryMessage(`No tens fitxes suficients. Necessites ${currentInitialChips} fitxes per jugar.`, 3000);
            navigate('/inici');
            return;
          }
          
          // Guardar chips_before_buy_in (NO descomptar encara)
          chipsBeforeBuyInRef.current = currentChips;
          
          console.log('🎮 PRÀCTICA - ENTRADA (buy-in es descomptarà quan comenci la partida):', {
            username: user.username,
            profileChips: currentChips,
            buyIn: currentInitialChips,
            chipsBeforeBuyIn: chipsBeforeBuyInRef.current
          });
        } else {
          console.log('❌ PRÀCTICA - Error al carregar perfil');
          showTemporaryMessage('Error al carregar les fitxes. Torna-ho a intentar.', 3000);
          navigate('/inici');
          return;
        }
      } else if (currentInitialChips > 0 && !user) {
        // Si es requereixen fitxes reals però no hi ha usuari, retornar a l'inici
        console.log('❌ PRÀCTICA - Usuari no autenticat per mode amb fitxes reals');
        showTemporaryMessage('Has de tenir una sessió iniciada per jugar amb fitxes reals.', 3000);
        navigate('/inici');
        return;
      }
      
      console.log('✅ PRÀCTICA - Configurant jugadors amb', gameChips, 'fitxes (initialChips:', currentInitialChips, ')');
      // Filtrar bots que no poden jugar més (han fet all-in i han perdut a rondes anteriors)
      players = players
        .filter(p => {
          if (p.isHuman) return true;
          // Si el bot està a la llista de bots que no poden jugar més, eliminar-lo
          if (botsOutRef.current.has(p.id)) {
            console.log(`🚫 Bot ${p.name} (${p.id}) no pot jugar més - eliminat de la partida`);
            return false;
          }
          return true;
        })
        .map(p => ({
          ...p,
          chips: gameChips,
          folded: false, // Reset folded per a la nova partida
          isAllIn: false // Reset all-in
        }));
      // Guardar les fitxes inicials
      const chipsRecord: Record<string, number> = {};
      players.forEach(p => {
        chipsRecord[p.id] = Math.max(0, p.chips);
      });
      setPlayerChips(chipsRecord);
    }
    
    // Rotar el dealer a la dreta cada nova partida
    const nextDealer = (currentDealerIndex + 1) % 4;
    setCurrentDealerIndex(nextDealer);
    console.log('🎲 PRÀCTICA - Inicialitzant joc amb dealer index:', nextDealer);
    
    // DESCOMPTAR BUY-IN QUAN LA PARTIDA COMENÇA (abans de initializeGame)
    if (currentInitialChips > 0 && user && chipsBeforeBuyInRef.current !== null) {
      const supabase = createClient();
      const newChips = Math.max(0, chipsBeforeBuyInRef.current - currentInitialChips);
      
      // Actualitzar fitxes a la base de dades
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ chips: newChips })
        .eq('username', user.username);
      
      if (updateError) {
        console.error('❌ PRÀCTICA - Error descomptant buy-in:', updateError);
        showTemporaryMessage('Error al descomptar el buy-in. Torna-ho a intentar.', 3000);
        navigate('/inici');
        return;
      }
      
      // Actualitzar el context
      await updateChips(newChips);
      
      console.log('💰 PRÀCTICA - Buy-in descomptat quan la partida comença:', {
        username: user.username,
        chipsBeforeBuyIn: chipsBeforeBuyInRef.current,
        buyIn: currentInitialChips,
        after: newChips
      });
    }
    
    // Verificar que les fitxes estiguin correctes abans d'inicialitzar
    console.log('🔍 PRÀCTICA - Verificant fitxes abans d\'inicialitzar:', {
      players: players.map(p => ({ name: p.name, chips: p.chips })),
      currentInitialChips,
      gameChips: currentInitialChips === 0 ? 200 : currentInitialChips
    });
    
    const newGame = initializeGame(players, nextDealer);
    
    // Verificar que les fitxes es preservin després d'inicialitzar
    console.log('🔍 PRÀCTICA - Verificant fitxes després d\'inicialitzar:', {
      players: newGame.players.map(p => ({ name: p.name, chips: p.chips }))
    });
    
    setGameState(newGame);
    setShowResult(false);
    setMessage(null);
    processingRef.current = false;
    console.log('✅ PRÀCTICA - Joc inicialitzat correctament');
    // NO marcar aquí hasStartedFirstGameRef, ja s'ha fet a startGame abans
    // Resetear el flag de sense fitxes quan comença una nova partida
    hasShownOutOfChipsRef.current = false;
    // Si l'usuari té fitxes de nou, sortir del mode espectador
    const humanPlayer = players.find(p => p.isHuman);
    if (humanPlayer && humanPlayer.chips > 0) {
      setIsSpectator(false);
    }
    
    // Alliberar el flag de protecció
    isStartingGameRef.current = false;
    } catch (error) {
      console.error('❌ PRÀCTICA - Error a startGame:', error);
      isStartingGameRef.current = false;
    }
  }, [currentDealerIndex, playerChips, user, gamesPlayed, showTemporaryMessage, navigate]);
  
  // Carregar configuració des de localStorage (ABANS de qualsevol altra cosa)
  useEffect(() => {
    const config = localStorage.getItem('practica_config');
    if (config) {
      try {
        const { initialChips: savedChips, totalGames: savedGames } = JSON.parse(config);
        if (savedChips !== undefined) {
          setInitialChips(savedChips);
          console.log('📋 PRÀCTICA - Configuració carregada:', { initialChips: savedChips, totalGames: savedGames });
        }
        if (savedGames !== undefined) setTotalGames(savedGames);
      } catch (error) {
        console.error('Error loading config:', error);
      }
    } else {
      console.log('📋 PRÀCTICA - No hi ha configuració guardada, usant valors per defecte (mode gratuït)');
    }
    // Sempre resetejar el comptador quan es carrega la configuració
    setGamesPlayed(0); // Començar a 0, representarà la ronda actual (0 = no ha començat, 1 = primera ronda, etc.)
    hasStartedFirstGameRef.current = false;
    isStartingGameRef.current = false; // Resetear el flag de protecció
    setPlayerChips({});
    chipsBeforeBuyInRef.current = null; // Resetear chips_before_buy_in
    botsOutRef.current.clear(); // Resetear la llista de bots que no poden jugar més
    configLoadedRef.current = true;
    setIsSpectator(false);
    hasShownOutOfChipsRef.current = false;
  }, []);
  
  // Tancar drawer amb animació
  const closeHandsGuide = useCallback(() => {
    setIsClosingHandsGuide(true);
    setTimeout(() => {
      setShowHandsGuide(false);
      setIsClosingHandsGuide(false);
    }, 300); // Durada de l'animació slide-down
  }, []);
  
  const closeSettings = useCallback(() => {
    setIsClosingSettings(true);
    setTimeout(() => {
      setShowSettings(false);
      setIsClosingSettings(false);
    }, 300);
  }, []);

  const closeInfo = useCallback(() => {
    setIsClosingInfo(true);
    setTimeout(() => {
      setShowInfo(false);
      setIsClosingInfo(false);
    }, 300);
  }, []);

  const handleExitGame = useCallback(async () => {
    // Gestió de fitxes al sortir (només si no és mode gratuït)
    if (gameState && user && initialChips > 0) {
      const humanPlayer = gameState.players.find(p => p.isHuman);
      if (humanPlayer) {
        // Les fitxes que et queden al joc (ja inclou el pot si has guanyat)
        const finalChipsInGame = Math.max(0, humanPlayer.chips);
        
        let newTotalChips: number;
        
        // Usar chips_before_buy_in si està disponible
        if (chipsBeforeBuyInRef.current !== null) {
          // Càlcul: chips_before_buy_in - initialChips + finalChipsInGame
          // El buy-in ja s'ha descomptat quan la partida comença, per tant:
          // - chips_before_buy_in = fitxes abans del buy-in
          // - initialChips = buy-in que ja s'ha descomptat
          // - finalChipsInGame = fitxes que tens al final del joc
          // - Total = chips_before_buy_in - initialChips + finalChipsInGame
          const buyInDeducted = initialChips > 0 ? initialChips : 0;
          newTotalChips = chipsBeforeBuyInRef.current - buyInDeducted + finalChipsInGame;
          
          console.log('🏁 PRÀCTICA - SORTIDA:', {
            username: user.username,
            chipsBeforeBuyIn: chipsBeforeBuyInRef.current,
            buyIn: initialChips,
            buyInDeducted,
            finalChipsInGame,
            profit: finalChipsInGame - buyInDeducted,
            newTotalChips,
            calculation: `${chipsBeforeBuyInRef.current} - ${buyInDeducted} + ${finalChipsInGame} = ${newTotalChips}`
          });
        } else {
          // Fallback: càlcul si chips_before_buy_in no existeix
          const supabase = createClient();
          const { data: profile } = await supabase
            .from('profiles')
            .select('chips')
            .eq('username', user.username)
            .single();
          
          // Si el buy-in ja s'ha descomptat, simplement afegir les fitxes finals
          const buyInDeducted = initialChips > 0 ? initialChips : 0;
          newTotalChips = (profile?.chips || 0) + finalChipsInGame;
          
          console.log('🏁 PRÀCTICA - SORTIDA (FALLBACK):', {
            username: user.username,
            profileChipsBefore: profile?.chips || 0,
            buyIn: initialChips,
            buyInDeducted,
            finalChipsInGame,
            profit: finalChipsInGame - buyInDeducted,
            newTotalChips,
            calculation: `${profile?.chips || 0} + ${finalChipsInGame} = ${newTotalChips}`
          });
        }
        
        await updateChips(Math.max(0, newTotalChips));
      }
    } else if (initialChips === 0) {
      // Mode gratuït: no es modifiquen les fitxes reals
      console.log('🏁 PRÀCTICA - MODE GRATUÏT: No es guanyen ni es perden fitxes reals');
    }
    
    // Resetear el ref quan surts
    chipsBeforeBuyInRef.current = null;
    navigate('/inici');
  }, [gameState, user, updateChips, navigate, initialChips]);
  
  // Tancar menú de raise quan es clica fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showRaiseMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('.raise-menu-container')) {
          setShowRaiseMenu(false);
        }
      }
    };
    
    if (showRaiseMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showRaiseMenu]);  
  // Iniciar automàticament (només després que la configuració s'hagi carregat)
  useEffect(() => {
    if (!gameState && configLoadedRef.current && !isStartingGameRef.current) {
      console.log('🚀 PRÀCTICA - Iniciant joc (startGame llegirà la configuració directament)');
      startGame();
    }
  }, [gameState, startGame]);
  
  // Detectar quan l'usuari es queda sense fitxes (només després de resoldre la partida)
  useEffect(() => {
    if (!gameState || !showResult || isSpectator) return; // Només comprovar quan showResult és true
    
    const humanPlayer = gameState.players.find(p => p.isHuman);
    // Comprovar si ha perdut (fitxes finals = 0) després de resoldre la partida
    const finalChips = playerChips[humanPlayer?.id || ''] || humanPlayer?.chips || 0;
    if (humanPlayer && finalChips === 0 && !hasShownOutOfChipsRef.current) {
      hasShownOutOfChipsRef.current = true;
      setShowOutOfChipsModal(true);
    }
  }, [gameState, showResult, isSpectator, playerChips]);
  
  // Processar torn de la IA
  const processBotTurn = useCallback(async () => {
    console.log('🤖 [Pràctica] processBotTurn called - processing:', processingRef.current, 'showResult:', showResult);
    
    if (!gameState || showResult) return;
    
    if (processingRef.current) {
      console.warn('⚠️ [Pràctica] processingRef is true, skipping');
      return;
    }
    
    const currentPlayer = gameState.players[gameState.currentPlayerIndex];
    if (currentPlayer.isHuman || currentPlayer.folded || currentPlayer.isAllIn) return;
    
    // Doble verificació: assegurar que NO és el torn de l'humà
    const humanIndex = gameState.players.findIndex(p => p.isHuman);
    if (gameState.currentPlayerIndex === humanIndex) {
      console.warn('Intent de processar torn humà com a bot - evitat');
      return;
    }
    
    console.log(`🤖 [Pràctica] Processing bot ${currentPlayer.name} turn`);
    processingRef.current = true;
    setIsProcessing(true);
    
    try {
      // Temps de pensar aleatori
      const thinkTime = BOT_THINK_TIME_MIN + Math.random() * (BOT_THINK_TIME_MAX - BOT_THINK_TIME_MIN);
      await new Promise(resolve => setTimeout(resolve, thinkTime));
      
      const decision = makeBotDecision(currentPlayer, gameState);
      
      // Aplicar acció
      const newState = processAction(
        gameState,
        gameState.currentPlayerIndex,
        decision.action,
        decision.amount
      );
      
      setGameState(newState);
      
      // Missatge de l'acció
      const actionMessages: Record<string, string> = {
        fold: `${currentPlayer.name} es retira`,
        check: `${currentPlayer.name} passa`,
        call: `${currentPlayer.name} iguala`,
        raise: `${currentPlayer.name} puja`,
      };
      showTemporaryMessage(actionMessages[decision.action]);
    } catch (error) {
      console.error('Error processant torn del bot:', error);
    } finally {
      // Sempre resetegem els flags, fins i tot si hi ha error
      processingRef.current = false;
      setIsProcessing(false);
    }
  }, [gameState, showResult, showTemporaryMessage]);
  
  // Gestió del flux del joc
  useEffect(() => {
    if (!gameState || showResult || processingRef.current) return;
    
    const activePlayers = getActivePlayers(gameState);
    
    // Si només queda un jugador, ha guanyat (això inclou quan algú abandona)
    if (activePlayers.length === 1) {
      const { winner, hand } = determineWinner(gameState);
      processingRef.current = true;
      setTimeout(() => {
        setGameState(prev => {
          if (!prev) return prev;
          // Assegurar que les cartes comunitàries estiguin visibles a l'última ronda
          const communityCards = prev.phase === 'river' 
            ? prev.communityCards.map(c => ({ ...c, hidden: false }))
            : prev.communityCards;
          
            const updatedPlayers = prev.players.map(p => {
              if (p.id === winner.id) {
                return { ...p, chips: Math.max(0, p.chips + prev.pot) };
              }
              // Si un bot ha fet all-in i ha perdut (fitxes = 0), marcar-lo com a "no pot jugar més"
              if (!p.isHuman && p.isAllIn && p.chips === 0) {
                botsOutRef.current.add(p.id); // Afegir a la llista de bots que no poden jugar més
                return { ...p, folded: true, chips: 0 };
              }
              return { ...p, chips: Math.max(0, p.chips) };
            });
          
          // Actualitzar fitxes guardades
          const newChips: Record<string, number> = {};
          updatedPlayers.forEach(p => {
            newChips[p.id] = Math.max(0, p.chips);
          });
          setPlayerChips(newChips);
          
          // Les fitxes es guardaran a la base de dades quan el jugador surti
          // No actualitzar aquí per evitar duplicació de pèrdues
          
          return {
            ...prev,
            players: updatedPlayers,
            winner,
            winningHand: hand,
            phase: 'showdown',
            communityCards
          };
        });
        setShowResult(true);
        processingRef.current = false;
      }, gameState.phase === 'river' ? SHOWDOWN_DELAY : 800);
      return;
    }
    
    // Comprovar si la ronda ha acabat
    if (isRoundComplete(gameState)) {
      if (gameState.phase === 'river') {
        // Showdown - sempre mostrar resum a l'última ronda, fins i tot si algú ha abandonat
        processingRef.current = true;
        setTimeout(() => {
          const { winner, hand } = determineWinner(gameState);
          setGameState(prev => {
            if (!prev) return prev;
            // Assegurar que les cartes comunitàries estiguin visibles
            const communityCards = prev.communityCards.map(c => ({ ...c, hidden: false }));
            
            const updatedPlayers = prev.players.map(p => {
              if (p.id === winner.id) {
                return { ...p, chips: p.chips + prev.pot };
              }
              // Si un bot ha fet all-in i ha perdut (fitxes = 0), marcar-lo com a "no pot jugar més"
              if (!p.isHuman && p.isAllIn && p.chips === 0) {
                botsOutRef.current.add(p.id); // Afegir a la llista de bots que no poden jugar més
                return { ...p, folded: true, chips: 0 };
              }
              return p;
            });
            
            // Actualitzar fitxes guardades
            const newChips: Record<string, number> = {};
            updatedPlayers.forEach(p => {
              newChips[p.id] = p.chips;
            });
            setPlayerChips(newChips);
            
            // Les fitxes es guardaran a la base de dades quan el jugador surti
            // No actualitzar aquí per evitar duplicació de pèrdues
            
            return {
              ...prev,
              players: updatedPlayers,
              winner,
              winningHand: hand,
              phase: 'showdown',
              communityCards
            };
          });
          setShowResult(true);
          // NO incrementar aquí, ja s'ha incrementat quan comença la partida
          processingRef.current = false;
        }, SHOWDOWN_DELAY);
      } else if (gameState.phase !== 'showdown') {
        // Avançar a la següent fase
        console.log('📍 Avançant de fase:', gameState.phase);
        processingRef.current = true; // Bloquejar mentre avancem
        setGameState(prev => {
          if (!prev) return prev;
          const newState = advancePhase(prev);
          const phaseNames: Record<string, string> = {
            flop: '🃏 Flop',
            turn: '🃏 Turn', 
            river: '🃏 River',
          };
          if (phaseNames[newState.phase]) {
            showTemporaryMessage(phaseNames[newState.phase]);
          }
          return newState;
        });
        // Desbloquejar després d'un petit delay
        setTimeout(() => {
          console.log('📍 Fase avançada, desbloqueiant processingRef');
          processingRef.current = false;
          setIsProcessing(false);
        }, 100);
      }
      return;
    }
    
    // Si és torn d'un bot, processar-lo
    const currentPlayer = gameState.players[gameState.currentPlayerIndex];
    const humanIndex = gameState.players.findIndex(p => p.isHuman);
    
    // Verificar que NO és el torn de l'humà abans de processar bot
    if (gameState.currentPlayerIndex !== humanIndex && 
        !currentPlayer.isHuman && 
        !currentPlayer.folded && 
        !currentPlayer.isAllIn) {
      // Petit delay per assegurar que l'estat s'ha actualitzat correctament
      setTimeout(() => {
        processBotTurn();
      }, 100);
    }
  }, [gameState, showResult, processBotTurn, showTemporaryMessage, user]);
  
  // Timeout de seguretat per desbloquejar si els bots es queden penjats
  useEffect(() => {
    if (!gameState || showResult || gameState.phase === 'showdown') return;
    
    const currentPlayer = gameState.players[gameState.currentPlayerIndex];
    const humanIndex = gameState.players.findIndex(p => p.isHuman);
    
    // Si és torn d'un bot, crear un timeout de seguretat
    if (gameState.currentPlayerIndex !== humanIndex && 
        !currentPlayer.isHuman && 
        !currentPlayer.folded && 
        !currentPlayer.isAllIn) {
      const BOT_TIMEOUT = 3000; // 3 segons
      console.log(`⏰ [Pràctica] Setting bot timeout for ${currentPlayer.name} (${BOT_TIMEOUT}ms)`);
      
      const timeout = setTimeout(() => {
        console.log(`⏰ [Pràctica] Bot timeout triggered for ${currentPlayer.name}`);
        const stillSamePlayer = gameState.players[gameState.currentPlayerIndex]?.id === currentPlayer.id;
        if (stillSamePlayer && !showResult && gameState.phase !== 'showdown') {
          console.warn(`⚠️ [Pràctica] Bot ${currentPlayer.name} bloquejat durant ${BOT_TIMEOUT}ms, FORÇANT DESBLOQUEIG...`);
          processingRef.current = false;
          setIsProcessing(false);
          // Forçar el torn del bot després de desbloquejar
          setTimeout(() => processBotTurn(), 100);
        }
      }, BOT_TIMEOUT);
      
      return () => {
        console.log(`⏰ [Pràctica] Clearing bot timeout for ${currentPlayer.name}`);
        clearTimeout(timeout);
      };
    }
  }, [gameState?.currentPlayerIndex, gameState?.phase, showResult, processBotTurn]);
  
  // Accions del jugador
  const handleAction = useCallback((action: 'fold' | 'check' | 'call' | 'raise', amount?: number) => {
    if (!gameState || isProcessing || showResult || isSpectator) return;
    
    const humanIndex = gameState.players.findIndex(p => p.isHuman);
    if (gameState.currentPlayerIndex !== humanIndex) {
      console.warn('Intent d\'acció fora de torn - evitat');
      return;
    }
    
    setIsProcessing(true);
    const newState = processAction(gameState, humanIndex, action, amount);
    
    // Delay després de l'acció de l'humà per evitar transicions massa ràpides
    setTimeout(() => {
      setGameState(newState);
      // Esperar un moment abans de permetre noves accions
      setTimeout(() => {
        setIsProcessing(false);
      }, 200);
    }, 400);
  }, [gameState, isProcessing, showResult, isSpectator]);
  
  const handleFold = () => handleAction('fold');
  const handleCheck = () => handleAction('check');
  const handleCall = () => handleAction('call');
  const handleRaise = (amount: number) => handleAction('raise', amount);
  
  if (!gameState) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-950">
        <div className="flex flex-col items-center gap-4">
          <div className="text-4xl animate-bounce">🃏</div>
          <div className="text-zinc-500 text-sm">Preparant la partida...</div>
        </div>
      </div>
    );
  }
  
  const humanPlayer = gameState.players.find(p => p.isHuman)!;
  const humanIndex = gameState.players.findIndex(p => p.isHuman);
  const isHumanTurn = gameState.currentPlayerIndex === humanIndex && !showResult && !humanPlayer.folded && !humanPlayer.isAllIn && !processingRef.current && !isSpectator;
  const toCall = gameState.currentBet - humanPlayer.bet;
  const canCheck = canPlayerCheck(gameState, humanIndex);
  
  // Calcular estadístiques amb outs
  const visibleCommunity = gameState.communityCards.filter(c => !c.hidden);
  const currentHand = evaluateHand(humanPlayer.cards, visibleCommunity);
  const activeOpponents = gameState.players.filter(p => !p.folded && !p.isHuman).length;
  const winProb = calculateWinProbability(humanPlayer.cards, visibleCommunity, activeOpponents);
  const { outs, draws } = calculateOuts(humanPlayer.cards, visibleCommunity);
  
  // Funció per arrodonir a múltiples de 5
  const roundToChipDenomination = (amount: number): number => {
    return Math.max(5, Math.round(amount / 5) * 5);
  };
  
  // Opcions de puja - arrodonides a múltiples de 5
  const minRaiseAmount = roundToChipDenomination(gameState.currentBet + gameState.minRaise);
  const potRaise = roundToChipDenomination(gameState.currentBet + gameState.pot);
  const halfPotRaise = roundToChipDenomination(gameState.currentBet + Math.round(gameState.pot / 2));
  
  // Noms de les fases
  const phaseNames: Record<string, string> = {
    preflop: 'Pre-flop',
    flop: 'Flop',
    turn: 'Turn',
    river: 'River',
    showdown: 'Final',
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col select-none overflow-x-hidden pb-safe touch-none overscroll-none pb-[180px] sm:pb-[200px]">
      {/* Header */}
      <div className="flex items-center justify-between px-3 sm:px-4 pt-3 sm:pt-4 pb-2 shrink-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="text-[10px] sm:text-xs font-semibold text-zinc-400 uppercase tracking-wider px-2 py-1 bg-zinc-800 rounded">
            {phaseNames[gameState.phase]}
          </span>
          {isHumanTurn && !isProcessing && (
            <span className="flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-semibold text-amber-400">
              <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="hidden sm:inline">El teu torn</span>
              <span className="sm:hidden">Tu</span>
            </span>
          )}
          {isProcessing && (
            <span className="text-[10px] sm:text-xs text-zinc-500">
              Pensant...
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-zinc-400 bg-zinc-800/80 px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-semibold">
            <span>{gamesPlayed > 0 ? gamesPlayed : 0}</span>
            <span className="text-zinc-600">/</span>
            <span>{totalGames}</span>
          </div>
          <button
            onClick={() => setShowInfo(true)}
            className="w-8 h-8 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center transition-colors"
            aria-label="Informació"
          >
            <Info className="w-4 h-4 text-zinc-400" />
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="w-8 h-8 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center transition-colors"
            aria-label="Configuració"
          >
            <Settings className="w-4 h-4 text-zinc-400" />
          </button>
        </div>
      </div>
      
      {/* Missatge temporal */}
      {message && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-40 animate-fade-in">
          <div className="bg-zinc-800 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
            {message}
          </div>
        </div>
      )}
      
      {/* Jugadors rivals */}
      <div className="pt-2 sm:pt-3 pb-0 shrink-0">
        <PlayersRow 
          players={gameState.players} 
          currentPlayerIndex={gameState.currentPlayerIndex}
          humanIndex={humanIndex}
        />
      </div>
      
      {/* Taula - Cartes comunitàries */}
      <div className="flex-1 flex flex-col items-center justify-center min-h-0 relative -mt-3 sm:-mt-4">
        <div className="flex flex-col items-center justify-center w-full px-3 sm:px-4">
          <div className="relative w-full flex justify-center mb-2 sm:mb-3">
            {/* Fons de la taula */}
            <div className="absolute -inset-8 sm:-inset-12 bg-gradient-to-b from-emerald-950/20 to-transparent rounded-full blur-3xl" />
            
            {/* Cartes */}
            <div className="relative flex gap-1 sm:gap-1.5 md:gap-2 justify-center items-center">
              {gameState.communityCards.map((card, index) => (
                <div
                  key={index}
                  className={cn(
                    'transition-all duration-700',
                    !card.hidden && 'animate-flip-in'
                  )}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <Card card={card} size="md" />
                </div>
              ))}
            </div>
          </div>
          
          {/* Pot total al centre */}
          <div className="flex flex-col items-center gap-1 sm:gap-1.5">
            <div className="flex items-center gap-2 bg-zinc-800/50 border border-zinc-700 px-3 py-1 sm:py-1.5 rounded-lg">
              <Coins className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-zinc-400 uppercase tracking-wider">Pot</span>
              <span className="font-bold text-base text-amber-400 tabular-nums">{gameState.pot}</span>
            </div>
            
            {/* Aposta actual */}
            {gameState.currentBet > 0 && (
              <div className="text-zinc-400 text-xs sm:text-sm">
                Aposta: <span className="font-bold text-white">{gameState.currentBet}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modal de resultat */}
      {showResult && gameState.winner && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-3 sm:p-4">
          <div className={cn(
            'rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center max-w-sm w-full relative overflow-hidden',
            gameState.winner.isHuman 
              ? 'bg-gradient-to-b from-amber-900/50 to-zinc-900' 
              : 'bg-zinc-900'
          )}>
            {gameState.winner.isHuman && (
              <Trophy className="w-8 h-8 sm:w-10 sm:h-10 text-amber-400 mx-auto mb-2 sm:mb-3" />
            )}
            <span className="text-5xl sm:text-6xl mb-3 sm:mb-4 block">{gameState.winner.avatar}</span>
            <h2 className="text-xl sm:text-2xl font-bold mb-1">
              {gameState.winner.isHuman ? 'Has guanyat!' : `${gameState.winner.name} guanya`}
            </h2>
            <p className="text-zinc-400 mb-3 sm:mb-4 text-xs sm:text-sm">{gameState.winningHand}</p>
            
            {/* Cartes comunitàries */}
            <div className="mb-3">
              <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1.5">Taula</div>
              <div className="flex justify-center gap-1.5">
                {gameState.communityCards.map((card, index) => {
                  const winningHandEval = evaluateHand(gameState.winner!.cards, gameState.communityCards);
                  const isWinningCard = winningHandEval.cards.some(wc => wc.suit === card.suit && wc.rank === card.rank);
                  return (
                    <div key={index} className={cn(isWinningCard && "ring-2 ring-amber-400 rounded-xl")}>
                      <Card card={{ ...card, hidden: false }} size="sm" />
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Cartes del guanyador */}
            <div className="mb-4">
              <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1.5">Cartes del guanyador</div>
              <div className="flex justify-center gap-2">
                {gameState.winner.cards.map((card, index) => {
                  const winningHandEval = evaluateHand(gameState.winner!.cards, gameState.communityCards);
                  const isWinningCard = winningHandEval.cards.some(wc => wc.suit === card.suit && wc.rank === card.rank);
                  return (
                    <div key={index} className={cn(isWinningCard && "ring-2 ring-amber-400 rounded-xl")}>
                      <Card card={{ ...card, hidden: false }} size="sm" />
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6 space-y-2">
              <p className="text-amber-400 font-bold text-xl sm:text-2xl flex items-center justify-center gap-2">
                <Coins className="w-4 h-4 sm:w-5 sm:h-5" />
                +{gameState.pot}
              </p>
              {/* Benefici de l'usuari humà */}
              {humanPlayer && (
                <div className="text-sm sm:text-base">
                  <span className="text-zinc-400">El teu benefici: </span>
                  <span className={cn(
                    "font-bold",
                    humanPlayer.id === gameState.winner.id 
                      ? "text-amber-400" 
                      : humanPlayer.totalBet > 0
                      ? "text-red-400"
                      : "text-zinc-400"
                  )}>
                    {humanPlayer.id === gameState.winner.id 
                      ? `+${gameState.pot}` 
                      : humanPlayer.totalBet > 0
                      ? `-${humanPlayer.totalBet}`
                      : "0"}
                  </span>
                </div>
              )}
            </div>
            {gamesPlayed < totalGames ? (
              <Button 
                onClick={startGame} 
                className="w-full gap-2 h-11 sm:h-12 text-sm sm:text-base font-semibold bg-white text-zinc-900 hover:bg-zinc-100"
              >
                <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4" />
                Següent ronda ({gamesPlayed + 1}/{totalGames})
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="text-center">
                  <p className="text-amber-400 font-semibold text-sm mb-1">🏆 Sessió completada!</p>
                  <p className="text-zinc-400 text-xs">{totalGames} rondes jugades</p>
                </div>
                <Button 
                  onClick={() => setShowFinalSummary(true)}
                  className="w-full gap-2 h-11 sm:h-12 text-sm sm:text-base font-semibold bg-white text-zinc-900 hover:bg-zinc-100"
                >
                  <Trophy className="w-3 h-3 sm:w-4 sm:h-4" />
                  Veure resultat final
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Zona inferior del jugador */}
      <div className="fixed bottom-0 left-0 right-0 px-2 sm:px-4 pb-2 sm:pb-4 pt-0.5 space-y-1.5 sm:space-y-2 bg-gradient-to-t from-zinc-900 via-zinc-900 to-zinc-900 z-30">
        {/* Indicador si el jugador s'ha retirat o està en mode espectador */}
        {humanPlayer.folded && !isSpectator && (
          <div className="text-center text-zinc-500 text-sm py-2">
            T'has retirat d'aquesta mà
          </div>
        )}
        {isSpectator && (
          <div className="text-center text-cyan-400 text-sm py-2 font-medium">
            Mode espectador - Veient la partida
          </div>
        )}
        
        {/* Botons d'acció */}
        {!humanPlayer.folded && !humanPlayer.isAllIn && (
          <div className="flex gap-1.5 sm:gap-2">
            {/* Fold */}
            <Button 
              onClick={handleFold}
              disabled={!isHumanTurn}
              variant="outline"
              className={cn(
                "h-10 sm:h-12 px-2 sm:px-4 font-semibold transition-all border-zinc-700 text-zinc-400 text-xs sm:text-base",
                "hover:bg-red-500/10 hover:border-red-500/50 hover:text-red-400",
                "disabled:opacity-40"
              )}
            >
              <X className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">Abandonar</span>
            </Button>
            
            {/* Check/Call */}
            {canCheck ? (
              <Button 
                onClick={handleCheck}
                disabled={!isHumanTurn}
                className={cn(
                  "flex-1 h-10 sm:h-12 text-xs sm:text-base font-semibold transition-all",
                  "bg-zinc-800 hover:bg-zinc-700 text-white",
                  isHumanTurn && "ring-2 ring-amber-400/50"
                )}
              >
                Passa
              </Button>
            ) : (
              <Button 
                onClick={handleCall}
                disabled={!isHumanTurn || humanPlayer.chips < toCall}
                className={cn(
                  "flex-1 h-10 sm:h-12 text-xs sm:text-base font-semibold transition-all",
                  "bg-zinc-800 hover:bg-zinc-700 text-white",
                  isHumanTurn && "ring-2 ring-amber-400/50"
                )}
              >
                Iguala <span className="ml-1 text-amber-400">{Math.min(toCall, humanPlayer.chips)}</span>
              </Button>
            )}
            
            {/* Raise amb menú */}
            <div className="flex-1 relative raise-menu-container">
              <Button 
                onClick={() => handleRaise(minRaiseAmount)}
                disabled={!isHumanTurn || humanPlayer.chips < minRaiseAmount - humanPlayer.bet}
                className={cn(
                  "w-full h-10 sm:h-12 text-xs sm:text-base font-semibold transition-all",
                  "bg-amber-500 hover:bg-amber-400 text-zinc-900",
                  "disabled:opacity-40 disabled:bg-zinc-700 disabled:text-zinc-500"
                )}
              >
                {gameState.currentBet === 0 ? 'Aposta' : 'Puja'} {minRaiseAmount}
              </Button>
              {isHumanTurn && !humanPlayer.folded && humanPlayer.chips > minRaiseAmount && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRaiseMenu(!showRaiseMenu)}
                  className="absolute -top-8 right-0 h-7 px-2 text-[10px] sm:text-xs text-zinc-400 hover:text-white hover:bg-zinc-800"
                >
                  + Opcions
                </Button>
              )}
              {/* Menú desplegable */}
              {showRaiseMenu && isHumanTurn && (
                <div className="absolute bottom-full right-0 mb-2 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl p-1 flex flex-col gap-1 z-50 min-w-32">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      handleRaise(halfPotRaise);
                      setShowRaiseMenu(false);
                    }}
                    disabled={humanPlayer.chips < halfPotRaise - humanPlayer.bet}
                    className="justify-between text-xs font-semibold text-zinc-300 hover:bg-zinc-700 hover:text-white disabled:opacity-40 h-8"
                  >
                    <span>½ Pot</span>
                    <span className="text-amber-400">{halfPotRaise}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      handleRaise(potRaise);
                      setShowRaiseMenu(false);
                    }}
                    disabled={humanPlayer.chips < potRaise - humanPlayer.bet}
                    className="justify-between text-xs font-semibold text-zinc-300 hover:bg-zinc-700 hover:text-white disabled:opacity-40 h-8"
                  >
                    <span>Pot</span>
                    <span className="text-amber-400">{potRaise}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      handleRaise(humanPlayer.bet + humanPlayer.chips);
                      setShowRaiseMenu(false);
                    }}
                    className="justify-between text-xs font-bold text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 h-8"
                  >
                    <span>💰 All-in</span>
                    <span>{humanPlayer.chips + humanPlayer.bet}</span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Cartes del jugador i Info */}
        <div className="flex gap-2 sm:gap-3 items-stretch">
          {/* Cartes del jugador */}
          <button
            onClick={() => setShowHandsGuide(true)}
            className={cn(
              "bg-zinc-900/80 rounded-xl sm:rounded-2xl p-2 sm:p-3 flex items-center justify-center border border-zinc-800 transition-all cursor-pointer shrink-0",
              "hover:border-amber-500/60 active:scale-95",
              isHumanTurn && "border-amber-500/40 shadow-lg shadow-amber-500/10",
              humanPlayer.folded && "opacity-50"
            )}
          >
            <CardStack cards={humanPlayer.cards} overlap={true} size="lg" highlighted={isHumanTurn} />
          </button>
        
        {/* Info del jugador: Fitxes i Estadístiques */}
          <div className="bg-zinc-900/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-zinc-800 flex-1 min-w-0 flex flex-col gap-3 sm:gap-4">
            {/* Primera fila: Fitxes i Mà */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 relative">
              {/* Separador vertical - primera barra */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-12 sm:h-16 bg-zinc-700/50" />
              
          {/* Fitxes */}
              <div className="flex flex-col relative z-10">
                <div className="text-zinc-500 text-[10px] sm:text-xs uppercase tracking-wider mb-1.5 font-medium">Fitxes</div>
                <div className="flex items-baseline gap-1.5">
                  <Coins className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" />
                  <div className="text-xl sm:text-3xl font-black text-white tabular-nums">{humanPlayer.chips}</div>
                </div>
            {humanPlayer.bet > 0 && (
                  <div className="text-zinc-400 text-[10px] sm:text-xs mt-1.5 font-medium">
                    Apostat: <span className="text-amber-400 font-semibold">{humanPlayer.bet}</span>
              </div>
            )}
          </div>
          
              {/* Mà actual */}
              {!humanPlayer.folded && visibleCommunity.length > 0 && (
                <div className="flex flex-col relative z-10">
                  <div className="text-zinc-500 text-[10px] sm:text-xs uppercase tracking-wider mb-1.5 font-medium">Mà actual</div>
                  <div className="text-zinc-100 text-sm sm:text-base font-bold leading-tight">{currentHand.rank}</div>
              </div>
              )}
                </div>
            
            {/* Segona fila: Probabilitats i Outs */}
            {!humanPlayer.folded && showProbabilities && visibleCommunity.length > 0 && (
              <div className="pt-2 border-t border-zinc-700/50">
                <div className="grid grid-cols-2 gap-3 sm:gap-4 relative">
                  {/* Separador vertical - segona barra */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-10 sm:h-14 bg-zinc-700/50" />
                  
                  {/* Probabilitats */}
                  <div className="flex flex-col relative z-10 min-w-0">
                    <div className="text-zinc-500 text-[9px] sm:text-xs uppercase tracking-wider mb-1 font-medium truncate">Probabilitats</div>
                      <div className={cn(
                      "text-lg sm:text-2xl font-black tabular-nums leading-none",
                        winProb > 60 ? "text-emerald-400" : winProb > 40 ? "text-amber-400" : "text-red-400"
                      )}>
                        {winProb}%
                      </div>
                    </div>
                  
                  {/* Outs */}
                    {visibleCommunity.length < 5 && outs > 0 && (
                      <div 
                        onClick={() => setShowOutsInfo(true)}
                      className="flex flex-col cursor-pointer group relative z-10 min-w-0"
                      >
                      <div className="text-zinc-500 text-[9px] sm:text-xs uppercase tracking-wider mb-1 font-medium group-hover:text-cyan-400 transition-colors truncate">Outs</div>
                      <div className="text-lg sm:text-2xl font-black text-cyan-400 tabular-nums group-hover:text-cyan-300 transition-colors leading-none">{outs}</div>
                      </div>
                    )}
                </div>
                  </div>
                )}
            
            {/* Estat quan està retirat o esperant */}
            {humanPlayer.folded && (
              <div className="text-zinc-500 text-xs sm:text-sm text-center py-2">
                T'has retirat d'aquesta mà
              </div>
            )}
            {!humanPlayer.folded && visibleCommunity.length === 0 && (
              <div className="text-zinc-500 text-xs sm:text-sm text-center py-2">
                Esperant flop...
              </div>
            )}
        </div>
        </div>
      </div>
      
      {/* Drawer de guia de mans */}
      {showHandsGuide && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in"
            onClick={closeHandsGuide}
          />
          
          {/* Drawer */}
          <div className={cn(
            "fixed bottom-0 left-0 right-0 z-50",
            isClosingHandsGuide ? "animate-slide-down" : "animate-slide-up"
          )}>
            <div className="bg-zinc-900 rounded-t-3xl shadow-2xl max-h-[85vh] overflow-y-auto">
              {/* Handle */}
              <div className="sticky top-0 bg-zinc-900 pt-3 pb-2 flex justify-center">
                <div className="w-12 h-1.5 bg-zinc-700 rounded-full" />
              </div>
              
              {/* Header */}
              <div className="px-4 sm:px-6 pb-4">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="text-xl sm:text-2xl font-bold text-white">Combinacions de Poker</h2>
                  <button
                    onClick={closeHandsGuide}
                    className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5 text-zinc-400" />
                  </button>
                </div>
                <p className="text-sm text-zinc-400">Ordenades de major a menor valor</p>
                {visibleCommunity.length > 0 && (
                  <div className="mt-2 text-xs text-amber-400 bg-amber-500/10 px-3 py-1.5 rounded-lg border border-amber-500/30">
                    La teva mà actual: <span className="font-bold">{currentHand.rank}</span>
                  </div>
                )}
              </div>
              
              {/* Llista de mans */}
              <div className="px-4 sm:px-6 pb-6 space-y-3">
                {[
                  {
                    rank: 'Escala de Color',
                    name: 'Escala de Color (Straight Flush)',
                    cards: ['9♠️', '10♠️', 'J♠️', 'Q♠️', 'K♠️'],
                    description: '5 cartes seguides i del mateix pal.',
                  },
                  {
                    rank: 'Poker',
                    name: 'Pòquer (Four of a Kind)',
                    cards: ['8♦️', '8♠️', '8♣️', '8♥️', '//', '2♠️'],
                    description: '4 cartes iguals (mateix número o lletra) + 1 carta qualsevol.',
                  },
                  {
                    rank: 'Full',
                    name: 'Full House (Full House)',
                    cards: ['A♣️', 'A♦️', 'A♠️', '//', '10♥️', '10♣️'],
                    description: '3 cartes iguals + 1 parella.',
                  },
                  {
                    rank: 'Color',
                    name: 'Color (Flush)',
                    cards: ['A♠️', 'J♠️', '9♠️', '6♠️', '2♠️'],
                    description: '5 cartes del mateix pal, però NO seguides.',
                  },
                  {
                    rank: 'Escala',
                    name: 'Escala (Straight)',
                    cards: ['5♣️', '6♦️', '7♠️', '8♥️', '9♣️'],
                    description: '5 cartes seguides, però de pals diferents.',
                  },
                  {
                    rank: 'Trio',
                    name: 'Trio (Three of a Kind)',
                    cards: ['Q♠️', 'Q♦️', 'Q♣️', '//', '9♥️', '4♠️'],
                    description: '3 cartes iguals + 2 cartes diferents.',
                  },
                  {
                    rank: 'Doble Parella',
                    name: 'Doble Parella (Two Pair)',
                    cards: ['K♠️', 'K♦️', '//', '4♣️', '4♥️', '//', '9♠️'],
                    description: '2 parelles diferents + 1 carta extra.',
                  },
                  {
                    rank: 'Parella',
                    name: 'Parella (One Pair)',
                    cards: ['J♣️', 'J♦️', '//', '8♠️', '5♥️', '2♦️'],
                    description: '2 cartes iguals + 3 cartes diferents.',
                  },
                  {
                    rank: 'Carta Alta',
                    name: 'Carta Alta (High Card)',
                    cards: ['A♠️', 'Q♦️', '9♣️', '6♥️', '3♦️'],
                    description: 'No hi ha cap combinació, compta la carta més alta.',
                  },
                ].map((hand, index) => {
                  const isCurrentHand = currentHand.rank === hand.rank;
                  return (
                    <div
                      key={index}
                      className={cn(
                        'rounded-xl p-3 sm:p-4 transition-all',
                        isCurrentHand 
                          ? 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border-2 border-amber-500 shadow-lg shadow-amber-500/20' 
                          : 'bg-zinc-800/50 border border-zinc-700/50'
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm',
                          isCurrentHand ? 'bg-amber-500 text-zinc-900' : 'bg-zinc-700 text-zinc-300'
                        )}>
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={cn(
                            'font-bold text-base sm:text-lg mb-1',
                            isCurrentHand ? 'text-amber-400' : 'text-white'
                          )}>
                            {hand.name}
                          </h3>
                          <div className="flex flex-wrap gap-1 mb-2">
                            {hand.cards.map((card, cardIndex) => {
                              // Si és el separador, mostrar una barra vertical
                              if (card === '//') {
                                return (
                                  <div
                                    key={cardIndex}
                                    className="flex items-center justify-center px-0.5"
                                  >
                                    <span className="text-zinc-600 text-lg font-bold">/</span>
                                  </div>
                                );
                              }
                              // Extreure el pal (inclou l'emoji amb variant selector)
                              const suitMatch = card.match(/[♠♥♦♣]️?$/);
                              const suit = suitMatch ? suitMatch[0] : '';
                              const rank = suit ? card.slice(0, -suit.length) : card;
                              const isRed = suit.includes('♥') || suit.includes('♦');
                              return (
                                <div
                                  key={cardIndex}
                                  className="w-9 h-12 sm:w-10 sm:h-14 bg-white rounded-md shadow flex flex-col items-center justify-center font-bold text-sm sm:text-base"
                                  style={{ color: isRed ? '#ef4444' : '#18181b' }}
                                >
                                  <span className="leading-none">{rank}</span>
                                  <span className="text-base sm:text-lg leading-none">{suit.replace('️', '')}</span>
                                </div>
                              );
                            })}
                          </div>
                          <p className={cn(
                            'text-xs sm:text-sm',
                            isCurrentHand ? 'text-amber-200' : 'text-zinc-400'
                          )}>
                            {hand.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Popup explicatiu dels Outs */}
      {showOutsInfo && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in"
            onClick={() => setShowOutsInfo(false)}
          />
          
          {/* Modal */}
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md">
            <div className="bg-zinc-900 rounded-2xl shadow-2xl p-5 sm:p-6 border border-zinc-800 animate-fade-in">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-cyan-400 mb-1">Què són els Outs?</h3>
                  <p className="text-sm text-zinc-400">Com calcular les teves probabilitats</p>
                </div>
                <button
                  onClick={() => setShowOutsInfo(false)}
                  className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>

              {/* Contingut */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-white mb-2">Definició</h4>
                  <p className="text-sm text-zinc-300 leading-relaxed">
                    Els <span className="text-cyan-400 font-semibold">outs</span> són les cartes que et queden al deck 
                    i que poden millorar la teva mà per guanyar la partida.
                  </p>
                </div>

                <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                  <h4 className="font-bold text-white mb-3">Com es calcula?</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-cyan-400 font-bold text-xs">
                        1
                      </div>
                      <p className="text-zinc-300 flex-1">
                        <span className="font-semibold text-white">Identifica les cartes</span> que completen la teva jugada (color, escala, trio, etc.)
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-cyan-400 font-bold text-xs">
                        2
                      </div>
                      <p className="text-zinc-300 flex-1">
                        <span className="font-semibold text-white">Compta quantes hi ha</span> d'aquestes cartes al deck (52 total menys les que ja has vist)
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-cyan-400 font-bold text-xs">
                        3
                      </div>
                      <p className="text-zinc-300 flex-1">
                        <span className="font-semibold text-white">Aplica la regla 4-2:</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 rounded-xl p-4 border border-amber-500/30">
                  <h4 className="font-bold text-amber-400 mb-2 flex items-center gap-2">
                    <span className="text-lg">📊</span>
                    Regla 4-2
                  </h4>
                  <div className="space-y-1.5 text-sm">
                    <p className="text-zinc-200">
                      <span className="font-bold text-amber-400">Al Flop:</span> Multiplica els outs × 4 = % de guanyar
                    </p>
                    <p className="text-zinc-200">
                      <span className="font-bold text-amber-400">Al Turn:</span> Multiplica els outs × 2 = % de guanyar
                    </p>
                  </div>
                </div>

                <div className="bg-zinc-800/30 rounded-xl p-3 border border-zinc-700/50">
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    <span className="font-semibold text-zinc-300">Exemple:</span> Si tens 9 outs al flop, 
                    tens aproximadament un <span className="text-cyan-400 font-semibold">36%</span> (9 × 4) de probabilitat 
                    de completar la teva mà.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Drawer de Configuració */}
      {showSettings && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in"
            onClick={closeSettings}
          />
          
          {/* Drawer */}
          <div className={cn(
            "fixed bottom-0 left-0 right-0 z-50",
            isClosingSettings ? "animate-slide-down" : "animate-slide-up"
          )}>
            <div className="bg-zinc-900 rounded-t-3xl shadow-2xl max-h-[85vh] overflow-y-auto">
              {/* Handle */}
              <div className="sticky top-0 bg-zinc-900 pt-3 pb-2 flex justify-center">
                <div className="w-12 h-1.5 bg-zinc-700 rounded-full" />
              </div>
              
              {/* Header */}
              <div className="px-4 sm:px-6 pb-4 border-b border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-cyan-400" />
                  <h2 className="text-xl sm:text-2xl font-bold text-white">Configuració</h2>
                </div>
                <button
                  onClick={closeSettings}
                  className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>

              <div className="p-4 sm:p-6 space-y-6">
                {/* Configuració */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">Configuració del Joc</h3>
                  <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                    {/* Toggle probabilitats */}
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-semibold text-zinc-300">
                          Mostrar probabilitats
                        </label>
                        <p className="text-xs text-zinc-500">Probabilitat de guanyar i outs</p>
                      </div>
                      <button
                        onClick={() => setShowProbabilities(!showProbabilities)}
                        className={cn(
                          "relative w-12 h-7 rounded-full transition-colors",
                          showProbabilities ? "bg-cyan-500" : "bg-zinc-700"
                        )}
                      >
                        <div className={cn(
                          "absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform",
                          showProbabilities ? "translate-x-6" : "translate-x-1"
                        )} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Sortir de la partida */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">Accions</h3>
                  <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                    <button
                      onClick={handleExitGame}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 text-red-400 rounded-lg font-semibold transition-colors"
                    >
                      <LogOut className="w-5 h-5" />
                      Sortir de la partida
                    </button>
                    <p className="text-xs text-zinc-500 mt-2 text-center">
                      Les teves fitxes es guardaran automàticament
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Drawer d'Informació */}
      {showInfo && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in"
            onClick={closeInfo}
          />
          
          {/* Drawer */}
          <div className={cn(
            "fixed bottom-0 left-0 right-0 z-50",
            isClosingInfo ? "animate-slide-down" : "animate-slide-up"
          )}>
            <div className="bg-zinc-900 rounded-t-3xl shadow-2xl max-h-[85vh] overflow-y-auto">
              {/* Handle */}
              <div className="sticky top-0 bg-zinc-900 pt-3 pb-2 flex justify-center">
                <div className="w-12 h-1.5 bg-zinc-700 rounded-full" />
              </div>
              
              {/* Header */}
              <div className="px-4 sm:px-6 pb-4 border-b border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-cyan-400" />
                  <h2 className="text-xl sm:text-2xl font-bold text-white">Informació del Joc</h2>
                </div>
                <button
                  onClick={closeInfo}
                  className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>

              <div className="p-4 sm:p-6 space-y-6">
                {/* Informació del joc */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">Informació del Joc</h3>
                  
                  {/* Objectiu */}
                  <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl p-4 border border-cyan-500/30 mb-4">
                    <h4 className="font-bold text-cyan-400 mb-2">OBJECTIU DEL JOC</h4>
                    <p className="text-sm text-zinc-200 leading-relaxed">
                      Fer la millor mà de 5 cartes utilitzant les teves 2 cartes més les 5 cartes comunitàries
                    </p>
                  </div>

                  {/* Rondes */}
                  <div className="space-y-3">
                    <h4 className="font-bold text-white text-sm uppercase tracking-wider mb-4">Rondes del Joc</h4>
                    
                    {/* Pre-flop */}
                    <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold text-sm">
                          1
                        </div>
                        <div className="flex-1">
                          <h5 className="font-bold text-amber-400 text-sm mb-1.5">Pre-flop</h5>
                          <p className="text-xs text-zinc-300 leading-relaxed mb-2">
                            Reps <span className="font-semibold text-white">2 cartes</span> privades. Hi ha small blind, big blind i dealer.
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Fold</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Call</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Raise</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Flop */}
                    <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold text-sm">
                          2
                        </div>
                        <div className="flex-1">
                          <h5 className="font-bold text-amber-400 text-sm mb-1.5">Flop</h5>
                          <p className="text-xs text-zinc-300 leading-relaxed mb-2">
                            Surten <span className="font-semibold text-white">3 cartes</span> comunitàries al centre de la taula.
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Check</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Bet</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Call</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Raise</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Fold</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Turn */}
                    <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold text-sm">
                          3
                        </div>
                        <div className="flex-1">
                          <h5 className="font-bold text-amber-400 text-sm mb-1.5">Turn</h5>
                          <p className="text-xs text-zinc-300 leading-relaxed mb-2">
                            Surt la <span className="font-semibold text-white">4a carta</span> comunitària. Nova ronda d'apostes.
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Check</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Bet</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Call</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Raise</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Fold</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* River */}
                    <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold text-sm">
                          4
                        </div>
                        <div className="flex-1">
                          <h5 className="font-bold text-amber-400 text-sm mb-1.5">River</h5>
                          <p className="text-xs text-zinc-300 leading-relaxed mb-2">
                            Surt la <span className="font-semibold text-white">5a carta</span> comunitària. Última ronda d'apostes.
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Check</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Bet</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Call</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Raise</span>
                            <span className="px-2 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-300 font-medium">Fold</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Showdown */}
                    <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold text-sm">
                          5
                        </div>
                        <div className="flex-1">
                          <h5 className="font-bold text-amber-400 text-sm mb-1.5">Showdown</h5>
                          <p className="text-xs text-zinc-300 leading-relaxed">
                            Es mostren les cartes dels jugadors. <span className="font-semibold text-white">Guanya la millor mà</span> de 5 cartes.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Nota */}
                    <div className="bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/30">
                      <div className="flex items-start gap-2">
                        <span className="text-lg flex-shrink-0">✅</span>
                        <p className="text-xs text-emerald-200 leading-relaxed">
                          <span className="font-bold">Guany directe:</span> Si tots els jugadors es retiren (fold) i només queda 1 jugador, aquest guanya automàticament sense arribar al showdown.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal de resum final */}
      {showFinalSummary && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-3 sm:p-4">
          <div className="bg-zinc-900 rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center max-w-md w-full relative overflow-hidden border border-zinc-800">
            <div className="mb-6">
              <Trophy className="w-12 h-12 sm:w-16 sm:h-16 text-amber-400 mx-auto mb-4" />
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Resultat Final
              </h2>
              <p className="text-zinc-400 text-sm">
                Resum de la sessió de {totalGames} rondes
              </p>
            </div>

            {/* Resum de fitxes */}
            <div className="bg-zinc-800/50 rounded-xl p-4 sm:p-6 mb-6 space-y-4 border border-zinc-700">
              {initialChips > 0 && user && chipsBeforeBuyInRef.current !== null ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">Fitxes inicials</span>
                    <span className="text-white font-semibold">{chipsBeforeBuyInRef.current.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">Buy-in descomptat</span>
                    <span className="text-red-400 font-semibold">-{initialChips.toLocaleString()}</span>
                  </div>
                  <div className="h-px bg-zinc-700 my-2" />
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">Fitxes després del buy-in</span>
                    <span className="text-white font-semibold">
                      {(chipsBeforeBuyInRef.current - initialChips).toLocaleString()}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm">Fitxes inicials (mode gratuït)</span>
                  <span className="text-white font-semibold">200</span>
                </div>
              )}
              
              <div className="h-px bg-zinc-700 my-2" />
              
              <div className="flex items-center justify-between">
                <span className="text-zinc-400 text-sm">Fitxes finals</span>
                <span className="text-white font-semibold">
                  {(playerChips[humanPlayer?.id || ''] || humanPlayer?.chips || 0).toLocaleString()}
                </span>
              </div>
              
              <div className="h-px bg-zinc-700 my-3" />
              
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-white">Benefici/Perduda</span>
                {(() => {
                  const initialChipsAfterBuyIn = initialChips > 0 && user && chipsBeforeBuyInRef.current !== null
                    ? chipsBeforeBuyInRef.current - initialChips
                    : 200; // Mode gratuït
                  const finalChips = playerChips[humanPlayer?.id || ''] || humanPlayer?.chips || 0;
                  const profit = finalChips - initialChipsAfterBuyIn;
                  return (
                    <span className={cn(
                      "text-2xl font-bold flex items-center gap-1",
                      profit > 0 ? "text-green-400" : profit < 0 ? "text-red-400" : "text-zinc-400"
                    )}>
                      {profit > 0 && "+"}
                      {profit.toLocaleString()}
                      <Coins className="w-5 h-5" />
                    </span>
                  );
                })()}
              </div>
            </div>

            {/* Missatge segons el resultat */}
            {(() => {
              const initialChipsAfterBuyIn = initialChips > 0 && user && chipsBeforeBuyInRef.current !== null
                ? chipsBeforeBuyInRef.current - initialChips
                : 200; // Mode gratuït
              const finalChips = playerChips[humanPlayer?.id || ''] || humanPlayer?.chips || 0;
              const profit = finalChips - initialChipsAfterBuyIn;
              
              if (profit > 0) {
                return (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
                    <p className="text-green-400 font-semibold">🎉 Enhorabona! Has guanyat fitxes</p>
                  </div>
                );
              } else if (profit < 0) {
                return (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
                    <p className="text-red-400 font-semibold">Has perdut fitxes. Més sort la propera vegada!</p>
                  </div>
                );
              } else {
                return (
                  <div className="bg-zinc-500/10 border border-zinc-500/30 rounded-xl p-4 mb-6">
                    <p className="text-zinc-400 font-semibold">Has empatat!</p>
                  </div>
                );
              }
            })()}

            {/* Botó per tornar a l'inici */}
            <Button 
              onClick={handleExitGame}
              className="w-full gap-2 h-12 text-base font-semibold bg-white text-zinc-900 hover:bg-zinc-100"
            >
              <LogOut className="w-4 h-4" />
              Tornar a l'inici
            </Button>
          </div>
        </div>
      )}

      {/* Modal de sense fitxes */}
      {showOutOfChipsModal && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 animate-fade-in"
            onClick={() => {}} // No tancar al clicar fora
          />
          
          {/* Modal */}
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md">
            <div className="bg-zinc-900 rounded-2xl shadow-2xl p-6 sm:p-8 border border-zinc-800 animate-fade-in">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 border-2 border-red-500/50 flex items-center justify-center">
                  <Coins className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                  T'has quedat sense fitxes
                </h3>
                <p className="text-sm text-zinc-400">
                  Has fet all-in i has perdut totes les teves fitxes en aquesta partida.
                </p>
              </div>

              {/* Opcions */}
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setIsSpectator(true);
                    setShowOutOfChipsModal(false);
                  }}
                  className="w-full h-12 text-base font-semibold bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"
                >
                  <Info className="w-5 h-5 mr-2" />
                  Veure la partida com a espectador
                </Button>
                
                <Button
                  onClick={handleExitGame}
                  className="w-full h-12 text-base font-semibold bg-white hover:bg-zinc-100 text-black"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  Tornar a l'inici
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Indicador de mode espectador */}
      {isSpectator && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-40 animate-fade-in">
          <div className="bg-zinc-800/90 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg border border-zinc-700 flex items-center gap-2">
            <Info className="w-4 h-4 text-cyan-400" />
            <span>Mode espectador - Veient la partida</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default PokerTable;
